/**
 * Palco lógico e zoom (§4.2.2): aplica no elemento do AppShell, via CSSOM (compatível com a CSP),
 * `zoom; width; height; position: fixed; left: 0; top: {topBarsH}px`. Desktop e TV com o zoom de layoutMode();
 * tablet e land com zoom 1 e palco = janela útil (§4.7.1: largura total, altura H); no flow nada é escrito (documento
 * rolável, §4.6.1). As faixas `.top-bars` ficam fora deste elemento, em px reais.
 * DE-24: o portal `#dialog-root` recebe o mesmo zoom, a mesma largura e o mesmo topo, com altura 0 (não cobre o app;
 * os diálogos se posicionam em px lógicos dentro dele e o véu usa `position: fixed`).
 * `toStage(clientX, clientY)` converte coordenadas de ponteiro para px lógicos (nunca offsetX/layerX).
 */
import type { RefObject } from 'preact';
import { useLayoutEffect } from 'preact/hooks';
import type { LayoutState } from '../model/types';
import { layout } from '../state/store';

export interface StageBox {
  zoom: number;
  /** Largura lógica (px). */
  width: number;
  /** Altura lógica (px). */
  height: number;
  /** Topo em px reais (soma das faixas do topo). */
  top: number;
}

const BOX_PROPS = ['zoom', 'width', 'height', 'position', 'left', 'top'] as const;

/**
 * Caixa do palco por modo; null no flow (o CSS do AppShell manda). `pageScroll`: com a tela escolhida, o tablet e o
 * celular deitado também rolam a página como o flow (ORD-F-052), e o palco não é fixado.
 */
export function stageBox(
  s: Pick<LayoutState, 'mode' | 'zoom' | 'stageW' | 'stageH'>,
  topBarsH: number,
  pageScroll = false,
): StageBox | null {
  if (s.mode === 'flow') return null;
  if (pageScroll && (s.mode === 'tablet' || s.mode === 'land')) return null;
  return { zoom: s.zoom, width: s.stageW, height: s.stageH, top: topBarsH };
}

/** Escreve (ou limpa, com null) a caixa do palco no elemento. `height` permite a altura 0 do portal dos diálogos. */
export function applyStageBox(el: HTMLElement, box: StageBox | null, height?: number): void {
  const st = el.style;
  if (!box) {
    for (const p of BOX_PROPS) st.removeProperty(p);
    return;
  }
  st.setProperty('zoom', String(box.zoom));
  st.setProperty('width', `${box.width}px`);
  st.setProperty('height', `${height ?? box.height}px`);
  st.setProperty('position', 'fixed');
  st.setProperty('left', '0px');
  st.setProperty('top', `${box.top}px`);
}

/** Elemento do palco registrado pelo AppShell (um por documento). */
let stageEl: HTMLElement | null = null;

/** Coordenadas do cliente → px lógicos do palco: `[(x − r.left) / zoom, (y − r.top) / zoom]` (§4.2.2). */
export function toStage(clientX: number, clientY: number): [number, number] {
  const zoom = layout.value.zoom || 1;
  const r = stageEl?.getBoundingClientRect();
  return [(clientX - (r?.left ?? 0)) / zoom, (clientY - (r?.top ?? 0)) / zoom];
}

/** Portal dos diálogos (DE-24), quando existe. */
function dialogRoot(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.getElementById('dialog-root');
}

/**
 * Com `ref` (só o AppShell): aplica a caixa no elemento e no `#dialog-root` a cada mudança de modo, zoom, palco ou
 * `topBarsH`. Sem argumentos (mapa, linha do tempo, tooltip): só lê. Devolve `toStage` e o zoom atual.
 */
export function useStageZoom(
  ref?: RefObject<HTMLElement>,
  topBarsH = 0,
  pageScroll = false,
): { toStage: typeof toStage; zoom: number } {
  const s = layout.value;
  const { mode, zoom, stageW, stageH } = s;
  useLayoutEffect(() => {
    const el = ref?.current;
    if (!el) return;
    stageEl = el;
    const box = stageBox({ mode, zoom, stageW, stageH }, topBarsH, pageScroll);
    applyStageBox(el, box);
    const portal = dialogRoot();
    if (portal) applyStageBox(portal, box, 0);
  }, [ref, mode, zoom, stageW, stageH, topBarsH, pageScroll]);
  useLayoutEffect(() => {
    const el = ref?.current ?? null;
    return () => {
      if (el && stageEl === el) stageEl = null;
    };
  }, [ref]);
  return { toStage, zoom };
}
